import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import {
  useAuditoria,
  useEtag,
  useUsuariDetall,
  type RegistreAuditoriaDto,
} from '@/api/administracio';
import { useDesaUsuari, type UsuariDetallPerfilDto } from '@/api/ajustosPersones';
import { Icona, type NomIcona } from '@/components/Icona';
import { useFormat } from '@/i18n/useFormat';
import { Toolbar } from '@/layout/Toolbar';
import { ErrorCarrega } from '@/pages/EstatsPagina';
import { Avatar, Buit } from '@/pages/operacio/comu';
import { useMe } from '@/permisos/MeContext';
import { textAccioAuditoria, textEntitatAuditoria } from './auditoria';
import { CanvisAuditoria } from './CanvisAuditoria';
import { CapPanell, FaTemps } from './comu';
import { useGuardaCanvis } from './marc/canvisContext';
import { AccessosUsuari } from './persones/AccessosUsuari';
import { PerfilUsuari } from './persones/PerfilUsuari';
import { SyncEntra } from './persones/SyncEntra';
import './administracio.css';
import './persones.css';

const COLOR_VERB: Record<string, string> = {
  alta: 'tl-item--green',
  nova: 'tl-item--green',
  modificar: 'tl-item--blue',
  edicio: 'tl-item--blue',
  baixa: 'tl-item--red',
  eliminar: 'tl-item--red',
  retirar: 'tl-item--orange',
  tancar: 'tl-item--purple',
  PERMIS_DENEGAT: 'tl-item--red',
};
const colorAccio = (accio: string) => COLOR_VERB[accio.split('.').pop() ?? ''] ?? '';

const ICONA_CANAL: Record<string, NomIcona> = {
  EMAIL: 'envelope',
  TRUCADA: 'phone',
  SMS: 'message',
  PUSH: 'bell',
  PUSH_CRITICA: 'bell-badge',
};

type Pestanya = 'perfil' | 'accessos' | 'contacte' | 'activitat';
const PESTANYES: Pestanya[] = ['perfil', 'accessos', 'contacte', 'activitat'];

function Activitat({ usuariId }: { usuariId: number }) {
  const { t } = useTranslation('administracio');
  const f = useFormat();
  // RF-004: l'endpoint filtra per l'entitat consultada (usuari {id}) en lloc de filtrar al client.
  const activitat = useAuditoria({ entitat: 'usuari', entitatId: usuariId, page: 0, size: 20 });
  const items: RegistreAuditoriaDto[] = activitat.data?.items ?? [];
  return (
    <div className="panel">
      <CapPanell titol={t('detallUsuari.activitat')} />
      {items.length === 0 ? (
        <Buit icona="clock-arrow">
          {activitat.isLoading ? t('comu.carregant') : t('detallUsuari.senseActivitat')}
        </Buit>
      ) : (
        <div className="panel__body">
          <ol className="timeline">
            {items.map((a) => (
              <li key={a.id} className={`tl-item ${colorAccio(a.accio)}`}>
                <div className="tl-time">{f.dataHora(a.at)}</div>
                <div className="tl-title">{textAccioAuditoria(a.accio, t)}</div>
                <div className="tl-text">
                  {textEntitatAuditoria(a.entitat, t)}
                  {a.entitatId ? ` #${a.entitatId}` : ''} ·{' '}
                  {t('auditoria.canvis.per', { qui: a.upn ?? '—' })}
                  {a.resultat !== 'OK'
                    ? ` · ${t('detallUsuari.resultatKo')}${a.codiError ? ` (${a.codiError})` : ''}`
                    : ''}
                </div>
                {a.resultat === 'OK' ? (
                  <CanvisAuditoria valorPrevi={a.valorPrevi} valorNou={a.valorNou} />
                ) : null}
              </li>
            ))}
          </ol>
        </div>
      )}
    </div>
  );
}

function Contacte({ d }: { d: UsuariDetallPerfilDto }) {
  const { t } = useTranslation('administracio');
  return (
    <div className="admin-2iguals">
      <div className="panel">
        <CapPanell titol={t('detallUsuari.canals')} />
        {d.canalsVerificats.length === 0 ? (
          <Buit icona="envelope">{t('detallUsuari.senseCanals')}</Buit>
        ) : (
          <ul className="llista panel__body" style={{ padding: '6px 16px' }}>
            {d.canalsVerificats.map((c) => (
              <li key={c.id}>
                <Icona nom={ICONA_CANAL[c.canal] ?? 'link'} className="c-blue icon-18" />
                <span className="llista__main truncate">{c.valor}</span>
                <span className={`badge badge--${c.verificat ? 'ok' : 'incident'}`}>
                  {c.verificat ? t('detallUsuari.verificat') : t('detallUsuari.noVerificat')}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
      <div className="panel">
        <CapPanell titol={t('detallUsuari.dispositius')} />
        {d.dispositius.length === 0 ? (
          <Buit icona="phone">{t('detallUsuari.senseDispositius')}</Buit>
        ) : (
          <ul className="llista panel__body" style={{ padding: '6px 16px' }}>
            {d.dispositius.map((di) => (
              <li key={di.id} data-dispositiu={di.id}>
                <Icona
                  nom={di.plataforma === 'ANDROID' ? 'android' : 'apple'}
                  className="icon-18"
                />
                <div className="llista__main">
                  <div className="llista__t">
                    {di.model ??
                      t(`versions.plataformes.${di.plataforma}`, { defaultValue: di.plataforma })}
                    {di.appVersio ? ` · ${di.appVersio}` : ''}
                  </div>
                  <div className="llista__s">
                    {di.alertesCritiques
                      ? t('detallUsuari.critiquesActives')
                      : t('detallUsuari.critiquesInactives')}
                  </div>
                </div>
                <span className={`badge badge--${di.actiu ? 'ok' : 'gray'}`}>
                  {di.actiu ? t('comu.actiu') : t('comu.inactiu')}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

/** Fitxa completa d'una persona: perfil editable, accessos, contacte i activitat. */
export function W21DetallUsuari() {
  const { t } = useTranslation('administracio');
  const f = useFormat();
  const { pot } = useMe();
  const { id } = useParams();
  const usuariId = Number(id);
  const detall = useUsuariDetall(usuariId);
  const { etag } = useEtag('/usuaris', Number.isFinite(usuariId) ? usuariId : null);
  const desa = useDesaUsuari(usuariId, etag);
  const guarda = useGuardaCanvis();
  const [pestanya, setPestanya] = useState<Pestanya>('perfil');
  const [sync, setSync] = useState(false);
  const d = detall.data as UsuariDetallPerfilDto | undefined;

  if (detall.isError || !d) {
    return (
      <section className="pantalla" data-screen="W-21">
        <Toolbar titol={t('detallUsuari.titol')} enrere="/admin/usuaris" />
        {detall.isError ? (
          <ErrorCarrega onReintenta={() => void detall.refetch()} />
        ) : (
          <Buit icona="person">{t('comu.carregant')}</Buit>
        )}
      </section>
    );
  }
  const u = d.usuari;
  const nom = `${u.nom} ${u.cognoms ?? ''}`.trim();
  const potUsuari = pot('cai.usuaris', 'MODIFICAR');
  const potRols = pot('cai.rols', 'MODIFICAR');
  const comptes: Partial<Record<Pestanya, number>> = {
    accessos: d.rols.length,
    contacte: d.canalsVerificats.length + d.dispositius.length,
  };
  // La fitxa del perfil es reinicia quan canvien les dades desades (desar, sincronitzar…).
  const clauPerfil = `${u.id}-${u.version ?? ''}-${u.ultimSync ?? ''}-${(u.campsLocals ?? []).join(',')}-${u.actiu}`;

  return (
    <section className="pantalla" data-screen="W-21">
      <Toolbar
        titol={nom}
        enrere="/admin/usuaris"
        subtitol={
          <span className="hstack gap-6">
            <Avatar nom={nom} color="bg-blue" />
            {u.carrec ?? u.email ?? ''}
            <span className="dim" aria-hidden="true">
              ·
            </span>
            <span
              className="dim nowrap"
              data-ultim-sync
              title={
                u.ultimSync
                  ? t('persones.fitxa.ultimSync', { data: f.dataHora(u.ultimSync) })
                  : undefined
              }
            >
              {u.ultimSync ? (
                <>
                  Entra ID · <FaTemps iso={u.ultimSync} />
                </>
              ) : (
                t('persones.fitxa.maiSync')
              )}
            </span>
          </span>
        }
        badges={
          <span className={`badge badge--${u.actiu ? 'ok' : 'gray'}`}>
            {u.actiu ? t('comu.actiu') : t('comu.inactiu')}
          </span>
        }
      >
        {potUsuari ? (
          <button type="button" className="btn btn--outline btn--s" onClick={() => setSync(true)}>
            <Icona nom="arrow-clockwise" />
            {t('persones.fitxa.sincronitza')}
          </button>
        ) : null}
      </Toolbar>
      <div className="fitxa-pestanyes" role="tablist" aria-label={nom}>
        {PESTANYES.map((p) => (
          <button
            key={p}
            type="button"
            role="tab"
            id={`usuari-tab-${p}`}
            aria-selected={pestanya === p}
            aria-controls={`usuari-panell-${p}`}
            onClick={() => p !== pestanya && guarda(() => setPestanya(p))}
          >
            {t(`persones.fitxa.pestanyes.${p}`)}
            {comptes[p] != null ? <span className="fitxa-pestanyes__n">{comptes[p]}</span> : null}
          </button>
        ))}
      </div>
      <div
        className="fitxa-cos"
        role="tabpanel"
        id={`usuari-panell-${pestanya}`}
        aria-labelledby={`usuari-tab-${pestanya}`}
      >
        {pestanya === 'perfil' ? (
          <PerfilUsuari
            key={clauPerfil}
            usuari={u}
            editable={potUsuari}
            desa={(cos) => desa.mutate(cos)}
            desant={desa.isPending}
            errorDesa={desa.error}
          />
        ) : null}
        {pestanya === 'accessos' ? (
          <AccessosUsuari usuariId={usuariId} nomPersona={nom} rols={d.rols} editable={potRols} />
        ) : null}
        {pestanya === 'contacte' ? <Contacte d={d} /> : null}
        {pestanya === 'activitat' ? <Activitat usuariId={usuariId} /> : null}
      </div>
      {sync ? (
        <SyncEntra
          usuariId={usuariId}
          nom={nom}
          onTanca={() => setSync(false)}
          onFet={() => {
            setSync(false);
            setPestanya('perfil');
          }}
        />
      ) : null}
    </section>
  );
}
